import { useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../lib/api';

export interface EntryImage {
  id: string;
  entryType: string;
  entryId: string;
  r2Key: string;
  filename: string;
  mimeType: string;
  fileSize: string;
  width: string | null;
  height: string | null;
  sortOrder: string;
  createdAt: string;
  publicUrl: string | null;
}

interface UseImagesResult {
  images: EntryImage[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useImages(entryType: string, entryId: string): UseImagesResult {
  const [images, setImages] = useState<EntryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchImages = useCallback(async () => {
    if (!entryType || !entryId) {
      setImages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        apiUrl(`/api/images?entryType=${encodeURIComponent(entryType)}&entryId=${encodeURIComponent(entryId)}`),
        { credentials: 'include' }
      );

      if (!res.ok) {
        throw new Error('Failed to fetch images');
      }

      const data = await res.json();
      setImages(data.data || []);
    } catch (err) {
      console.error('Fetch images error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load images');
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [entryType, entryId]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  return { images, loading, error, refetch: fetchImages };
}

/**
 * Get the display URL for an image - uses the public R2 URL
 */
export function getDisplayUrl(image: EntryImage): string {
  if (!image.publicUrl) {
    console.warn('Image missing publicUrl:', image.id);
    return '';
  }
  return image.publicUrl;
}
